import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import {
  fetchAllBillboards,
  fetchBillboardCatalog,
  fetchBillboardDetail,
  fetchDynamicVenues,
  fetchNearbyBillboards,
  type BillboardListParams,
  type NearbyBillboardsParams,
  type VenueHierarchyFilters,
} from '@/features/billboards/api/billboardApi';

export function useAllBillboards(params: BillboardListParams, enabled = true) {
  return useQuery({
    queryKey: queryKeys.billboards.list(params),
    queryFn: () => fetchAllBillboards(params),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useBillboardCatalog(params: BillboardListParams, enabled = false) {
  const catalogParams = {
    city: params.city,
    type: params.type,
    search: params.search,
  };
  return useQuery({
    queryKey: [...queryKeys.billboards.root, 'catalog', catalogParams] as const,
    queryFn: () => fetchBillboardCatalog(catalogParams),
    placeholderData: keepPreviousData,
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useDynamicVenues(
  filters: VenueHierarchyFilters | string = {},
  selectedCity?: string,
) {
  const city =
    typeof filters === 'string'
      ? selectedCity
      : filters.city || selectedCity;

  return useQuery({
    queryKey: [...queryKeys.billboards.root, 'hierarchy-v2', city] as const,
    queryFn: () => fetchDynamicVenues(filters, selectedCity),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
}

export function useBillboardDetail(billboardId?: string) {
  return useQuery({
    queryKey: queryKeys.billboards.detail(billboardId ?? ''),
    queryFn: () => fetchBillboardDetail(billboardId!),
    enabled: Boolean(billboardId),
  });
}

export function useNearbyBillboards(params?: NearbyBillboardsParams) {
  return useQuery({
    queryKey: queryKeys.billboards.nearby(params),
    queryFn: () => fetchNearbyBillboards(params),
    enabled: Boolean(params?.lat) && Boolean(params?.lng),
    refetchOnWindowFocus: false,
  });
}
